import GuideArticle from '../components/GuideArticle'
import type { RelatedCalc, HowToStep } from '../components/GuideArticle'
import type { QA } from '../components/Faq'
import { useLocale, localizedHref } from '../i18n/locale'

interface Content {
  title: string
  h1: string
  description: string
  category: string
  faqs: QA[]
  related: RelatedCalc[]
  // Step-by-step data for HowTo schema. Mirrors the visible "Build order"
  // <ol> in the body but as plain text so Google's rich result can render
  // the step cards.
  steps: HowToStep[]
}

const STEPS_EN: HowToStep[] = [
  { name: 'Lay out the footprint and dig footings', text: 'Mark out the deck footprint and dig footings below the local frost line so the deck does not heave in winter.' },
  { name: 'Pour footings and set post anchors', text: 'Pour the concrete footings and set post anchors or brackets to keep posts off the concrete.' },
  { name: 'Set posts and build the beams', text: 'Install the pressure-treated posts, then build up beams from treated dimensional lumber and mount them to carry the joists.' },
  { name: 'Attach the ledger and hang the joists', text: 'Fasten the ledger board with proper flashing for attached decks, then hang the joists on hangers, typically 16 inches on-centre.' },
  { name: 'Fasten the decking boards and add fascia', text: 'Run decking boards perpendicular to the joists with exterior-rated screws or hidden fasteners, then finish the outer edges with rim and fascia boards.' },
  { name: 'Build railings and stairs, then seal', text: 'Install railing posts, rails and balusters (required above a certain height), build stair stringers, treads and risers, then apply sealant or stain to protect the finished deck.' },
]

const STEPS_FR: HowToStep[] = [
  { name: 'Tracer l\'empreinte et creuser les semelles', text: 'Tracez l\'empreinte de la terrasse et creusez les semelles sous la ligne de gel locale pour que la terrasse ne se soulève pas l\'hiver.' },
  { name: 'Couler les semelles et poser les ancrages', text: 'Coulez les semelles en béton et posez les ancrages ou supports de poteaux pour garder les poteaux hors du béton.' },
  { name: 'Installer les poteaux et monter les poutres', text: 'Installez les poteaux traités sous pression, puis assemblez les poutres à partir de bois de charpente traité et montez-les pour porter les solives.' },
  { name: 'Fixer la lambourde et suspendre les solives', text: 'Fixez la lambourde d\'appui avec un solin adéquat pour les terrasses attachées, puis suspendez les solives sur des étriers, généralement à 16 po entre axes.' },
  { name: 'Fixer les planches de terrasse et ajouter le fascia', text: 'Posez les planches de terrasse perpendiculairement aux solives avec des vis ou attaches cachées homologuées pour l\'extérieur, puis finissez les bordures avec des planches de rive et de fascia.' },
  { name: 'Construire les garde-corps et les escaliers, puis sceller', text: 'Installez les poteaux, lisses et barreaux de garde-corps (obligatoires au-delà d\'une certaine hauteur), construisez les limons, marches et contremarches d\'escalier, puis appliquez un scellant ou une teinture.' },
]

const EN: Content = {
  title: 'Deck Building Materials List: Everything You Need | RenoSheets',
  h1: 'Deck Building Materials List',
  description:
    'A complete materials checklist for building a deck — footings, posts, beams, joists, decking, fasteners, railings and stairs — plus how the pieces fit together.',
  category: 'Outdoor',
  related: [
    { label: 'Deck Calculator', href: '/calculators/deck/', blurb: 'Decking boards, joists, posts and concrete for your deck size.' },
    { label: 'Concrete Calculator', href: '/calculators/concrete/', blurb: 'Bags or yards for footings and post holes.' },
    { label: 'Fence Calculator', href: '/calculators/fence/', blurb: 'Adding a railing run or privacy fence? Get posts and pickets.' },
  ],
  steps: STEPS_EN,
  faqs: [
    {
      q: 'How far apart should deck joists be?',
      a: 'Deck joists are most commonly spaced 16 inches on-centre, which suits standard decking run perpendicular to the joists. Some composite boards and diagonal layouts call for 12 inches on-centre for extra support. Always follow the decking manufacturer’s span tables and your local code.',
    },
    {
      q: 'What lumber should I use for a deck?',
      a: 'Use pressure-treated lumber rated for the application — ground-contact rated for posts and any framing near the soil, and standard treated or composite for joists and decking. Stainless or hot-dipped galvanized fasteners and hangers resist the corrosive chemicals in treated wood. Composite decking is lower-maintenance but still rides on a treated wood frame.',
    },
    {
      q: 'How many decking boards do I need?',
      a: 'Divide the deck area by the coverage of one board (its length × actual width, accounting for the gap between boards), then add about 10% for waste and cuts. The Deck Calculator does this from your deck dimensions and also estimates joists, posts, and the concrete for footings.',
    },
    {
      q: 'Do I need a permit to build a deck?',
      a: 'Most jurisdictions require a permit for decks above a certain height or area, and for any deck attached to the house. Rules vary widely, so check with your local building department before you start — an attached ledger board and footing depth are common inspection points.',
    },
    {
      q: 'How deep should deck footings be?',
      a: 'Footings generally must extend below the local frost line so the deck does not heave in winter — that depth varies a lot by region. Your building department publishes the required frost depth and footing size for your area; this is one of the most important details to get right and to confirm before pouring.',
    },
  ],
}

const FR: Content = {
  title: 'Liste de matériaux pour construire une terrasse : tout ce qu’il vous faut | RenoSheets',
  h1: 'Liste de matériaux pour construire une terrasse',
  description:
    'Une liste de matériaux complète pour construire une terrasse — semelles, poteaux, poutres, solives, planches de terrasse, attaches, garde-corps et escaliers — et comment les pièces s’assemblent.',
  category: 'Extérieur',
  related: [
    { label: 'Calculateur de terrasse', href: '/calculators/deck/', blurb: 'Planches de terrasse, solives, poteaux et béton selon la taille de votre terrasse.' },
    { label: 'Calculateur de béton', href: '/calculators/concrete/', blurb: 'Sacs ou verges pour les semelles et les trous de poteaux.' },
    { label: 'Calculateur de clôture', href: '/calculators/fence/', blurb: 'Vous ajoutez une section de garde-corps ou une clôture d’intimité? Obtenez poteaux et planches.' },
  ],
  steps: STEPS_FR,
  faqs: [
    {
      q: 'À quelle distance doivent être espacées les solives de terrasse?',
      a: 'Les solives de terrasse sont le plus souvent espacées de 16 pouces entre axes, ce qui convient aux planches de terrasse standards posées perpendiculairement aux solives. Certaines planches composites et les poses en diagonale exigent 12 pouces entre axes pour un soutien additionnel. Suivez toujours les tableaux de portée du fabricant de planches et votre code local.',
    },
    {
      q: 'Quel bois dois-je utiliser pour une terrasse?',
      a: 'Utilisez du bois traité sous pression homologué pour l’usage — homologué contact avec le sol pour les poteaux et toute charpente près du sol, et du bois traité standard ou du composite pour les solives et les planches de terrasse. Des attaches et des étriers en acier inoxydable ou galvanisés à chaud résistent aux produits chimiques corrosifs du bois traité. La planche de terrasse composite demande moins d’entretien, mais repose tout de même sur une charpente en bois traité.',
    },
    {
      q: 'Combien de planches de terrasse me faut-il?',
      a: 'Divisez la surface de la terrasse par la couverture d’une planche (sa longueur × sa largeur réelle, en tenant compte de l’espace entre les planches), puis ajoutez environ 10 % pour les pertes et les coupes. Le Calculateur de terrasse fait ce calcul à partir des dimensions de votre terrasse et estime aussi les solives, les poteaux et le béton pour les semelles.',
    },
    {
      q: 'Faut-il un permis pour construire une terrasse?',
      a: 'La plupart des municipalités exigent un permis pour les terrasses au-delà d’une certaine hauteur ou superficie, et pour toute terrasse attachée à la maison. Les règles varient grandement; vérifiez donc auprès de votre service d’urbanisme local avant de commencer — une lambourde d’appui attachée et la profondeur des semelles sont des points d’inspection courants.',
    },
    {
      q: 'À quelle profondeur doivent être les semelles de terrasse?',
      a: 'Les semelles doivent généralement descendre sous la ligne de gel locale pour que la terrasse ne se soulève pas l’hiver — cette profondeur varie beaucoup d’une région à l’autre. Votre service d’urbanisme publie la profondeur de gel et la taille de semelle requises pour votre secteur; c’est l’un des détails les plus importants à bien faire et à confirmer avant de couler.',
    },
  ],
}

export default function GuideDeckMaterials() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <GuideArticle
      title={c.title}
      h1={c.h1}
      description={c.description}
      path="/guides/deck-building-materials-list/"
      updated="2026-06-04"
      category={c.category}
      faqs={c.faqs}
      related={c.related}
      howToSteps={c.steps}
    >
      {locale === 'fr' ? (
        <>
          <p>
            Une terrasse est un empilement de pièces simples qui dépendent toutes les unes des autres — les semelles portent
            les poteaux, les poteaux portent les poutres, les poutres portent les solives, les solives portent les planches de
            terrasse. Dressez une liste de matériaux complète dès le départ et la construction se déroule sans accroc.
            Utilisez le <a href={L('/calculators/deck/')}>Calculateur de terrasse</a> pour transformer la taille de votre
            terrasse en quantités de planches, de solives, de poteaux et de béton, puis consultez cette liste pour que rien ne manque.
          </p>

          <h2>Fondation et charpente</h2>
          <ul>
            <li><strong>Béton</strong> pour les semelles et les trous de poteaux — taille et profondeur selon la ligne de gel locale. Estimez avec le <a href={L('/calculators/concrete/')}>Calculateur de béton</a>.</li>
            <li><strong>Coffrages de semelle</strong> (tubes de coffrage) et <strong>ancrages ou supports de poteaux</strong> pour garder les poteaux hors du béton.</li>
            <li><strong>Poteaux</strong> — traités sous pression et homologués contact avec le sol, généralement 4×4 ou 6×6.</li>
            <li><strong>Poutres</strong> — assemblées à partir de bois de charpente traité pour porter les solives.</li>
            <li><strong>Lambourde d’appui</strong> (pour les terrasses attachées) avec un solin adéquat pour empêcher l’eau d’entrer dans la maison.</li>
            <li><strong>Solives</strong> et <strong>étriers à solive</strong> — solives habituellement à 16 po entre axes.</li>
          </ul>

          <h2>Surface de la terrasse</h2>
          <ul>
            <li><strong>Planches de terrasse</strong> — traitées sous pression, cèdre ou composite. Posées perpendiculairement aux solives.</li>
            <li><strong>Vis de terrasse ou attaches cachées</strong> homologuées pour l’extérieur et le bois traité.</li>
            <li><strong>Planches de rive et de fascia</strong> pour finir les bordures extérieures.</li>
          </ul>

          <h2>Garde-corps et escaliers</h2>
          <ul>
            <li><strong>Poteaux de garde-corps, lisses et barreaux</strong> (obligatoires au-delà d’une certaine hauteur — vérifiez le code).</li>
            <li><strong>Limons, marches et contremarches d’escalier</strong> pour tout changement de niveau.</li>
            <li>Capuchons de poteaux et quincaillerie ou connecteurs de garde-corps.</li>
          </ul>

          <h2>Attaches et quincaillerie</h2>
          <ul>
            <li><strong>Vis de structure, boulons et clous d’étrier à solive</strong> galvanisés à chaud ou en acier inoxydable (les attaches ordinaires se corrodent dans le bois traité).</li>
            <li>Boulons ou tire-fonds de lambourde et ruban à solin pour les terrasses attachées.</li>
            <li>Scellant ou teinture pour protéger la terrasse finie.</li>
          </ul>

          <h2>Ordre de construction en un coup d’œil</h2>
          <ol>
            <li>Tracez l’empreinte et creusez les semelles sous la ligne de gel.</li>
            <li>Coulez les semelles et posez les ancrages de poteaux.</li>
            <li>Installez les poteaux, puis assemblez et montez les poutres.</li>
            <li>Fixez la lambourde d’appui (terrasses attachées), puis suspendez les solives.</li>
            <li>Fixez les planches de terrasse, puis ajoutez le fascia.</li>
            <li>Construisez les garde-corps et les escaliers, puis scellez ou teignez.</li>
          </ol>

          <p>
            Suivez chaque étape sur le <a href={L('/kanban-board/')}>tableau Kanban</a> gratuit pour aligner les inspections
            de semelles, les livraisons de matériaux et les fenêtres météo sans perdre le fil.
          </p>
        </>
      ) : (
        <>
          <p>
            A deck is a stack of simple parts that all depend on each other — footings carry posts, posts carry beams, beams
            carry joists, joists carry the deck boards. Get the materials list complete up front and the build goes smoothly.
            Use the <a href={L('/calculators/deck/')}>Deck Calculator</a> to turn your deck size into board, joist, post, and
            concrete quantities, then check this list so nothing's missing.
          </p>

          <h2>Foundation &amp; framing</h2>
          <ul>
            <li><strong>Concrete</strong> for footings/post holes — size and depth per local frost line. Estimate with the <a href={L('/calculators/concrete/')}>Concrete Calculator</a>.</li>
            <li><strong>Footing forms</strong> (tube forms) and <strong>post anchors/brackets</strong> to keep posts off the concrete.</li>
            <li><strong>Posts</strong> — ground-contact rated pressure-treated, typically 4×4 or 6×6.</li>
            <li><strong>Beams</strong> — built up from treated dimensional lumber to carry the joists.</li>
            <li><strong>Ledger board</strong> (for attached decks) plus proper flashing to keep water out of the house.</li>
            <li><strong>Joists</strong> and <strong>joist hangers</strong> — joists usually 16 in on-centre.</li>
          </ul>

          <h2>Decking surface</h2>
          <ul>
            <li><strong>Decking boards</strong> — pressure-treated, cedar, or composite. Run perpendicular to the joists.</li>
            <li><strong>Deck screws or hidden fasteners</strong> rated for exterior/treated lumber.</li>
            <li><strong>Rim/fascia boards</strong> to finish the outer edges.</li>
          </ul>

          <h2>Railings &amp; stairs</h2>
          <ul>
            <li><strong>Railing posts, rails, and balusters</strong> (required above a certain height — check code).</li>
            <li><strong>Stair stringers, treads, and risers</strong> for any change in level.</li>
            <li>Post caps and railing hardware/connectors.</li>
          </ul>

          <h2>Fasteners &amp; hardware</h2>
          <ul>
            <li>Hot-dipped galvanized or stainless <strong>structural screws, bolts, and joist-hanger nails</strong> (ordinary fasteners corrode in treated lumber).</li>
            <li>Ledger bolts/lag screws and flashing tape for attached decks.</li>
            <li>Sealant or stain to protect the finished deck.</li>
          </ul>

          <h2>Build order at a glance</h2>
          <ol>
            <li>Lay out the footprint and dig footings below the frost line.</li>
            <li>Pour footings and set post anchors.</li>
            <li>Set posts, then build and mount the beams.</li>
            <li>Attach the ledger (attached decks), then hang the joists.</li>
            <li>Fasten the decking boards, then add fascia.</li>
            <li>Build railings and stairs, then seal or stain.</li>
          </ol>

          <p>
            Track each stage on the free <a href={L('/kanban-board/')}>kanban board</a> so you can line up footing inspections,
            material deliveries, and weather windows without losing the thread.
          </p>
        </>
      )}
    </GuideArticle>
  )
}
